import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ClipboardList, TrendingUp, TrendingDown, CheckCircle2, Clock, XCircle,
  Plus, Edit2, Trash2, Search, Calendar, User, ChevronLeft, ChevronRight,
  Target, AlertCircle, MessageSquare, BarChart3, Megaphone, Users, Settings,
  FileText, ArrowRight, Eye, Filter, Tag, Sparkles, RefreshCw, X, Check,
  ArrowUpRight, ArrowDownRight, Minus, Image as ImageIcon, Camera, Zap, PieChart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, subMonths, addMonths, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';

// Section Header Component
function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <p className="text-xs text-[#9CA3AF]">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }) {
  const config = {
    pending: { label: 'Pending', color: '#F59E0B', icon: Clock },
    inProgress: { label: 'In Progress', color: '#3B82F6', icon: RefreshCw },
    effective: { label: 'Effective', color: '#10B981', icon: CheckCircle2 },
    notEffective: { label: 'Not Effective', color: '#E63946', icon: XCircle },
    cancelled: { label: 'Cancelled', color: '#6B7280', icon: X }
  };
  const { label, color, icon: Icon } = config[status] || config.pending;
  return (
    <Badge className="text-[10px] px-2 py-0.5 flex items-center gap-1 font-semibold" style={{ backgroundColor: `${color}20`, color, border: `1px solid ${color}40` }}>
      <Icon size={10} />
      {label}
    </Badge>
  );
}

// Priority Badge
function PriorityBadge({ priority }) {
  const config = {
    high: { label: 'High', color: '#E63946' },
    medium: { label: 'Medium', color: '#F59E0B' },
    low: { label: 'Low', color: '#10B981' }
  };
  const { label, color } = config[priority] || config.medium;
  return (
    <Badge className="text-[9px] px-1.5 py-0 font-medium" style={{ backgroundColor: `${color}15`, color }}>
      {label}
    </Badge>
  );
}

// Issue Type Tags by Department
const issueTypes = {
  marketing: [
    { id: 'ads_reject', label: 'Ads Reject', color: '#E63946' },
    { id: 'lead_issues', label: 'Lead Issues', color: '#F59E0B' },
    { id: 'landing_page', label: 'Landing Page', color: '#3B82F6' },
    { id: 'bot_issue', label: 'Bot Issue', color: '#8B5CF6' },
    { id: 'content', label: 'Content', color: '#10B981' },
    { id: 'campaign', label: 'Campaign', color: '#EC4899' },
    { id: 'seo', label: 'SEO', color: '#06B6D4' },
    { id: 'ctr_drop', label: 'CTR Drop', color: '#F97316' },
    { id: 'cpc_spike', label: 'CPC Spike', color: '#EF4444' },
    { id: 'budget_issue', label: 'Budget/Overspend', color: '#D946EF' },
    { id: 'targeting', label: 'Targeting/Audience', color: '#14B8A6' },
    { id: 'creative_fatigue', label: 'Creative Fatigue', color: '#A855F7' },
    { id: 'platform_issue', label: 'Platform Issue (FB/TT/Google)', color: '#6366F1' },
    { id: 'funnel_leak', label: 'Funnel Leak', color: '#F43F5E' },
    { id: 'mkt_other', label: 'Others (Marketing)', color: '#6B7280' },
  ],
  sales: [
    { id: 'conversion', label: 'Conversion', color: '#10B981' },
    { id: 'followup', label: 'Follow Up', color: '#F59E0B' },
    { id: 'closing', label: 'Closing', color: '#E63946' },
    { id: 'retention', label: 'Retention', color: '#3B82F6' },
    { id: 'upsell', label: 'Upsell', color: '#8B5CF6' },
    { id: 'script', label: 'Script', color: '#EC4899' },
    { id: 'lead_quality', label: 'Lead Quality', color: '#F97316' },
    { id: 'response_time', label: 'Slow Response Time', color: '#EF4444' },
    { id: 'pipeline_stuck', label: 'Pipeline Stuck', color: '#D946EF' },
    { id: 'deposit_issue', label: 'Deposit Issue', color: '#14B8A6' },
    { id: 'client_churn', label: 'Client Churn', color: '#A855F7' },
    { id: 'kyc_issue', label: 'KYC/Onboarding Issue', color: '#6366F1' },
    { id: 'sales_other', label: 'Others (Sales)', color: '#6B7280' },
  ],
  operation: [
    { id: 'process', label: 'Process', color: '#3B82F6' },
    { id: 'system', label: 'System', color: '#E63946' },
    { id: 'workflow', label: 'Workflow', color: '#10B981' },
    { id: 'reporting', label: 'Reporting', color: '#F59E0B' },
    { id: 'compliance', label: 'Compliance', color: '#8B5CF6' },
    { id: 'training', label: 'Training', color: '#EC4899' },
    { id: 'broker_issue', label: 'Broker Issue', color: '#F97316' },
    { id: 'signal_accuracy', label: 'Signal Accuracy', color: '#EF4444' },
    { id: 'channel_content', label: 'Channel Content Quality', color: '#D946EF' },
    { id: 'subscriber_drop', label: 'Subscriber Drop', color: '#14B8A6' },
    { id: 'commission_issue', label: 'Commission Issue', color: '#A855F7' },
    { id: 'lot_decline', label: 'Traded Lot Decline', color: '#6366F1' },
    { id: 'ops_other', label: 'Others (Operation)', color: '#6B7280' },
  ]
};

// Metrics by Department
const metricOptions = {
  marketing: [
    'Clicks Conversion', 'LPV Conversion', 'CTA Conversion', 'Subs Conversion', 'Leads Conversion',
    'CTR', 'CPC', 'CPL', 'CPS', 'CPS (Free Trial)', 'CPM', 'CPLPV',
    'ROAS', 'Engagement Rate', 'Reach', 'Impressions', 'Ad Frequency',
  ],
  sales: [
    'Closing Rate', 'Conversion Rate', 'Response Time', 'Follow Up Rate',
    'Average Deal Size', 'Average Deposit', 'Retention Rate', 'Renewal Rate',
    'Lead-to-Close Time', 'Revenue Per Client', 'Client Lifetime Value',
  ],
  operation: [
    'Avg Lot/Client', 'Com/Lot', 'Active Client Rate', 'Churn Rate',
    'E.R (Public)', 'E.R (VIP)', 'Signal Win Rate', 'Avg Views/Post',
    'Subscriber Growth Rate', 'Content Post Frequency', 'Compliance Score',
  ]
};

// Result Indicator
function ResultIndicator({ before, after }) {
  if (!after || after === '-') return null;
  const beforeNum = parseFloat(before) || 0;
  const afterNum = parseFloat(after) || 0;
  const diff = afterNum - beforeNum;
  const isPositive = diff > 0;
  const isNeutral = diff === 0;
  
  return (
    <div className={`flex items-center gap-1 text-xs font-mono ${isNeutral ? 'text-[#6B7280]' : isPositive ? 'text-[#10B981]' : 'text-[#E63946]'}`}>
      {isNeutral ? <Minus size={12} /> : isPositive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {isNeutral ? 'No change' : `${isPositive ? '+' : ''}${diff.toFixed(2)}`}
    </div>
  );
}

export default function LogContent({ ib }) {
  // States
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeDepartment, setActiveDepartment] = useState('marketing');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [viewingLog, setViewingLog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Form state
  const defaultForm = {
    date: format(new Date(), 'yyyy-MM-dd'),
    author: '',
    issueType: '',
    status: 'pending',
    priority: 'medium',
    impact: 'medium',
    title: '',
    description: '',
    actionTaken: '',
    metric: '',
    currentScore: '',
    expectedOutcome: '',
    endResult: '',
    remarks: '',
    reviewDate: '',
    otherIssueType: '',
    attachments: [],
  };
  const [formData, setFormData] = useState(defaultForm);
  
  // NO MOCK DATA - All IBs use real data only
  const DATA_VERSION = 'v3_mongodb';
  
  // Log database — loaded from MongoDB API (individual entries)
  const [logs, setLogs] = useState({ marketing: [], sales: [], operation: [] });
  const [logsLoaded, setLogsLoaded] = useState(false);
  const logsRef = React.useRef(logs);
  useEffect(() => { logsRef.current = logs; }, [logs]);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

  // Load logs from API
  useEffect(() => {
    setLogsLoaded(false);
    setLogs({ marketing: [], sales: [], operation: [] });
    const loadLogs = async () => {
      if (!ib?.id) return;
      try {
        const res = await axios.get(`${API_BASE}/api/channel-logs/${ib.id}`, { headers: getHeaders(), timeout: 10000 });
        const apiLogs = res.data.logs || { marketing: [], sales: [], operation: [] };
        setLogs(apiLogs);
        logsRef.current = apiLogs;
      } catch (err) {
        console.error('Failed to load logs:', err);
      }
      setLogsLoaded(true);
    };
    loadLogs();
  }, [ib?.id]);
  
  // Get logs for current month
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  
  const getLogsForMonth = (dept) => {
    return (logs[dept] || []).filter(log => {
      const logDate = parseISO(log.date);
      return isWithinInterval(logDate, { start: monthStart, end: monthEnd });
    });
  };
  
  const getAllLogsForMonth = () => {
    return [
      ...getLogsForMonth('marketing'),
      ...getLogsForMonth('sales'),
      ...getLogsForMonth('operation')
    ];
  };
  
  // Metrics calculations
  const metrics = useMemo(() => {
    const allLogs = getAllLogsForMonth();
    const byDept = {
      marketing: getLogsForMonth('marketing'),
      sales: getLogsForMonth('sales'),
      operation: getLogsForMonth('operation')
    };
    
    const countByStatus = (arr) => ({
      total: arr.length,
      pending: arr.filter(l => l.status === 'pending').length,
      inProgress: arr.filter(l => l.status === 'inProgress').length,
      effective: arr.filter(l => l.status === 'effective').length,
      notEffective: arr.filter(l => l.status === 'notEffective').length,
      critical: arr.filter(l => l.impact === 'critical').length,
      avgResolutionDays: (() => {
        const resolved = arr.filter(l => l.resolvedAt && l.createdAt);
        if (resolved.length === 0) return 0;
        const totalDays = resolved.reduce((sum, l) => {
          const created = new Date(l.createdAt || l.date);
          const resolvedAt = new Date(l.resolvedAt);
          return sum + Math.max(0, Math.round((resolvedAt - created) / 86400000));
        }, 0);
        return Math.round(totalDays / resolved.length);
      })(),
    });
    
    return {
      all: countByStatus(allLogs),
      marketing: countByStatus(byDept.marketing),
      sales: countByStatus(byDept.sales),
      operation: countByStatus(byDept.operation)
    };
  }, [logs, currentMonth]);
  
  // Filtered logs for current department
  const filteredLogs = useMemo(() => {
    let result = getLogsForMonth(activeDepartment);
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l => 
        l.title.toLowerCase().includes(q) || 
        l.description.toLowerCase().includes(q) ||
        l.author.toLowerCase().includes(q)
      );
    }
    
    if (filterStatus !== 'all') {
      result = result.filter(l => l.status === filterStatus);
    }
    
    if (filterType !== 'all') {
      result = result.filter(l => l.issueType === filterType);
    }
    
    // Sort by date descending
    return result.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [logs, activeDepartment, currentMonth, searchQuery, filterStatus, filterType]);
  
  // CRUD operations — Individual API calls (same pattern as daily data)
  const handleAddLog = async () => {
    if (!formData.title || !formData.author) return;
    
    const newLog = {
      ...formData,
      id: Date.now(),
      department: activeDepartment,
      createdAt: format(new Date(), 'yyyy-MM-dd HH:mm'),
      updatedAt: format(new Date(), 'yyyy-MM-dd'),
      resolvedAt: (formData.status === 'effective' || formData.status === 'notEffective') ? format(new Date(), 'yyyy-MM-dd') : null,
    };
    delete newLog.attachments;
    
    try {
      await axios.post(`${API_BASE}/api/channel-logs/${ib.id}`, newLog, { headers: getHeaders(), timeout: 10000 });
      setLogs(prev => ({ ...prev, [activeDepartment]: [...(prev[activeDepartment] || []), newLog] }));
      toast.success('Log saved');
    } catch (err) {
      console.error('Add log failed:', err?.response?.status, err?.response?.data);
      toast.error('Failed to save — try again');
      return;
    }
    
    setFormData(defaultForm);
    setShowAddModal(false);
  };
  
  const handleEditLog = async () => {
    if (!editingLog || !formData.title) return;
    
    const updateData = { ...formData, department: activeDepartment };
    delete updateData.attachments;
    delete updateData.id;
    delete updateData.ib_id;
    delete updateData.created_by;
    
    try {
      await axios.put(`${API_BASE}/api/channel-logs/${ib.id}/${editingLog.id}`, updateData, { headers: getHeaders(), timeout: 10000 });
      setLogs(prev => ({
        ...prev,
        [activeDepartment]: prev[activeDepartment].map(l => 
          l.id === editingLog.id ? { ...formData, id: l.id, updatedAt: format(new Date(), 'yyyy-MM-dd') } : l
        )
      }));
      toast.success('Log updated');
    } catch (err) {
      console.error('Edit log failed:', err?.response?.status, err?.response?.data);
      toast.error('Failed to update — try again');
      return;
    }
    
    setEditingLog(null);
    setFormData(defaultForm);
    setShowAddModal(false);
  };
  
  const handleDeleteLog = async (logId) => {
    try {
      await axios.delete(`${API_BASE}/api/channel-logs/${ib.id}/${logId}`, { headers: getHeaders(), timeout: 10000 });
      setLogs(prev => ({ ...prev, [activeDepartment]: prev[activeDepartment].filter(l => l.id !== logId) }));
    } catch (err) {
      console.error('Delete log failed:', err);
      toast.error('Failed to delete');
    }
  };
  
  // Quick status update — individual PUT
  const handleQuickStatusUpdate = async (logId, newStatus) => {
    const update = { status: newStatus };
    if (newStatus === 'effective' || newStatus === 'notEffective') {
      const log = logs[activeDepartment].find(l => l.id === logId);
      if (log && !log.resolvedAt) update.resolvedAt = format(new Date(), 'yyyy-MM-dd');
    }
    try {
      await axios.put(`${API_BASE}/api/channel-logs/${ib.id}/${logId}`, update, { headers: getHeaders(), timeout: 10000 });
      setLogs(prev => ({
        ...prev,
        [activeDepartment]: prev[activeDepartment].map(l => 
          l.id === logId ? { ...l, ...update, updatedAt: format(new Date(), 'yyyy-MM-dd') } : l
        )
      }));
    } catch (err) {
      console.error('Status update failed:', err);
      toast.error('Failed to update status');
    }
  };
  
  const openEditModal = (log) => {
    setEditingLog(log);
    setFormData(log);
    setShowAddModal(true);
  };
  
  const openViewModal = (log) => {
    setViewingLog(log);
    setShowViewModal(true);
  };
  
  // Department config
  const deptConfig = {
    marketing: { label: 'Marketing', icon: Megaphone, color: '#E63946' },
    sales: { label: 'Sales', icon: Users, color: '#10B981' },
    operation: { label: 'Operation', icon: Settings, color: '#3B82F6' }
  };
  
  return (
    <>
      <div className="p-6 space-y-6">
        {/* Header - No button here, moved to Log Entries */}
        <div>
          <h1 className="text-2xl font-bold text-white">Optimization Log</h1>
          <p className="text-sm text-[#9CA3AF]">Track adjustments, optimizations & results by department</p>
        </div>
        
        {/* Overview Metrics */}
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <SectionHeader 
            icon={BarChart3} 
            title="Log Overview" 
            subtitle={`${format(currentMonth, 'MMMM yyyy')} • ${metrics.all.total} total entries`}
          />
          
          <div className="p-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardList size={18} className="text-[#3B82F6]" />
                  <span className="text-[10px] uppercase text-[#6B7280]">Total Logs</span>
                </div>
                <p className="text-3xl font-bold text-white font-mono">{metrics.all.total}</p>
              </div>
              <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={18} className="text-[#F59E0B]" />
                  <span className="text-[10px] uppercase text-[#F59E0B]">Pending</span>
                </div>
                <p className="text-3xl font-bold text-white font-mono">{metrics.all.pending}</p>
              </div>
              <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw size={18} className="text-[#3B82F6]" />
                  <span className="text-[10px] uppercase text-[#3B82F6]">In Progress</span>
                </div>
                <p className="text-3xl font-bold text-white font-mono">{metrics.all.inProgress}</p>
              </div>
              <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 size={18} className="text-[#10B981]" />
                  <span className="text-[10px] uppercase text-[#10B981]">Effective</span>
                </div>
                <p className="text-3xl font-bold text-white font-mono">{metrics.all.effective}</p>
              </div>
              <div className="bg-[#E63946]/10 border border-[#E63946]/30 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle size={18} className="text-[#E63946]" />
                  <span className="text-[10px] uppercase text-[#E63946]">Not Effective</span>
                </div>
                <p className="text-3xl font-bold text-white font-mono">{metrics.all.notEffective}</p>
              </div>
            </div>
            
            {/* Department Breakdown */}
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(deptConfig).map(([key, config]) => {
                const Icon = config.icon;
                const deptMetrics = metrics[key];
                return (
                  <div key={key} className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${config.color}20`, border: `1px solid ${config.color}40` }}>
                        <Icon size={18} style={{ color: config.color }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{config.label}</p>
                        <p className="text-xs text-[#6B7280]">{deptMetrics.total} logs this month</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-[hsl(var(--surface))] rounded-full h-2 overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all"
                          style={{ 
                            width: deptMetrics.total > 0 ? `${(deptMetrics.effective / deptMetrics.total) * 100}%` : '0%',
                            backgroundColor: config.color 
                          }}
                        />
                      </div>
                      <span className="text-xs text-[#6B7280]">
                        {deptMetrics.total > 0 ? Math.round((deptMetrics.effective / deptMetrics.total) * 100) : 0}% effective
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Resolution Timeline + Issue Distribution */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              {/* Resolution Timeline */}
              <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={16} className="text-[#8B5CF6]" />
                  <span className="text-xs font-semibold text-white">Resolution Timeline</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-[hsl(var(--text-muted))]">Avg Resolution</span>
                    <span className="text-sm font-bold text-[#8B5CF6] font-mono">{metrics.all.avgResolutionDays} days</span>
                  </div>
                  {Object.entries(deptConfig).map(([key, config]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-[10px] text-[hsl(var(--text-muted))]">{config.label}</span>
                      <span className="text-xs font-mono" style={{ color: config.color }}>{metrics[key].avgResolutionDays}d</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t border-[hsl(var(--border))]/30">
                    <span className="text-[10px] text-[hsl(var(--text-muted))]">Critical Issues</span>
                    <span className="text-sm font-bold text-[#E63946] font-mono">{metrics.all.critical}</span>
                  </div>
                </div>
              </div>

              {/* Issue Type Distribution */}
              <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <PieChart size={16} className="text-[#3B82F6]" />
                  <span className="text-xs font-semibold text-white">Issue Distribution</span>
                </div>
                <div className="space-y-1.5 max-h-[140px] overflow-y-auto">
                  {(() => {
                    const allMonthLogs = getAllLogsForMonth();
                    const typeCounts = {};
                    allMonthLogs.forEach(l => {
                      const dept = ['marketing', 'sales', 'operation'].find(d => (logs[d] || []).some(ll => ll.id === l.id));
                      const types = dept ? issueTypes[dept] : [];
                      const typeInfo = types.find(t => t.id === l.issueType);
                      const label = l.issueType?.includes('other') ? (l.otherIssueType || 'Others') : (typeInfo?.label || l.issueType || 'Unknown');
                      const color = typeInfo?.color || '#6B7280';
                      typeCounts[label] = typeCounts[label] || { count: 0, color };
                      typeCounts[label].count++;
                    });
                    return Object.entries(typeCounts).sort((a, b) => b[1].count - a[1].count).map(([label, data]) => (
                      <div key={label} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: data.color }} />
                        <span className="text-[10px] text-[hsl(var(--text-muted))] flex-1 truncate">{label}</span>
                        <div className="flex items-center gap-1.5">
                          <div className="w-16 h-1.5 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${(data.count / allMonthLogs.length) * 100}%`, backgroundColor: data.color }} />
                          </div>
                          <span className="text-[9px] font-mono text-white">{data.count}</span>
                        </div>
                      </div>
                    ));
                  })()}
                  {getAllLogsForMonth().length === 0 && <p className="text-[10px] text-[hsl(var(--text-muted))] text-center py-4">No data</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Log Entries Section */}
        <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
          <SectionHeader 
            icon={FileText} 
            title="Log Entries" 
            subtitle={`${filteredLogs.length} entries in ${deptConfig[activeDepartment].label}`}
            action={
              <div className="flex items-center gap-3">
                {/* Month Navigation */}
                <div className="flex items-center gap-1 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg px-2 py-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0 text-[#9CA3AF] hover:text-white"
                    onClick={() => setCurrentMonth(prev => subMonths(prev, 1))}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="text-sm font-medium text-white px-3 min-w-[140px] text-center">
                    {format(currentMonth, 'MMMM yyyy')}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-7 w-7 p-0 text-[#9CA3AF] hover:text-white"
                    onClick={() => setCurrentMonth(prev => addMonths(prev, 1))}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
                {/* Add Log Entry Button */}
                <Button 
                  className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white shadow-lg h-9"
                  onClick={() => { setEditingLog(null); setFormData({ ...defaultForm, date: format(new Date(), 'yyyy-MM-dd') }); setShowAddModal(true); }}
                >
                  <Plus size={16} className="mr-2" />Add Log Entry
                </Button>
              </div>
            }
          />
          
          <div className="p-6">
            {/* Department Tabs */}
            <Tabs value={activeDepartment} onValueChange={setActiveDepartment} className="w-full">
              <div className="flex items-center justify-between mb-4">
                <TabsList className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]">
                  {Object.entries(deptConfig).map(([key, config]) => {
                    const Icon = config.icon;
                    const count = metrics[key].total;
                    return (
                      <TabsTrigger 
                        key={key} 
                        value={key} 
                        className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E63946] data-[state=active]:to-[#F97316] data-[state=active]:text-white px-4 gap-2"
                      >
                        <Icon size={14} />
                        {config.label}
                        <Badge className="ml-1 text-[10px] bg-white/20 text-inherit">{count}</Badge>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
                
                {/* Filters */}
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                    <Input 
                      placeholder="Search logs..." 
                      value={searchQuery} 
                      onChange={(e) => setSearchQuery(e.target.value)} 
                      className="pl-10 w-48 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white h-9" 
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-32 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white h-9">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="all" className="text-white">All Status</SelectItem>
                      <SelectItem value="pending" className="text-white">Pending</SelectItem>
                      <SelectItem value="inProgress" className="text-white">In Progress</SelectItem>
                      <SelectItem value="effective" className="text-white">Effective</SelectItem>
                      <SelectItem value="notEffective" className="text-white">Not Effective</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-32 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white h-9">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      <SelectItem value="all" className="text-white">All Types</SelectItem>
                      {issueTypes[activeDepartment]?.map(type => (
                        <SelectItem key={type.id} value={type.id} className="text-white">{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Log Cards */}
              {Object.keys(deptConfig).map(dept => (
                <TabsContent key={dept} value={dept} className="mt-0">
                  {filteredLogs.length === 0 ? (
                    <div className="text-center py-16 text-[#6B7280]">
                      <ClipboardList size={48} className="mx-auto mb-3 opacity-50" />
                      <p className="text-lg font-medium">No logs found</p>
                      <p className="text-sm">Add your first log entry for {format(currentMonth, 'MMMM yyyy')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredLogs.map(log => {
                        const typeConfig = issueTypes[dept]?.find(t => t.id === log.issueType);
                        return (
                          <div 
                            key={log.id} 
                            className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-[hsl(var(--border-hover))] transition-all group"
                          >
                            {/* Card Header */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#3B82F6]/20 to-[#8B5CF6]/20 flex items-center justify-center border border-[#3B82F6]/30">
                                  {log.status === 'effective' ? (
                                    <CheckCircle2 size={20} className="text-[#10B981]" />
                                  ) : log.status === 'notEffective' ? (
                                    <XCircle size={20} className="text-[#E63946]" />
                                  ) : log.status === 'inProgress' ? (
                                    <RefreshCw size={20} className="text-[#3B82F6]" />
                                  ) : (
                                    <Clock size={20} className="text-[#F59E0B]" />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="text-base font-semibold text-white">{log.title}</h4>
                                    <StatusBadge status={log.status} />
                                    {typeConfig && (
                                      <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}>
                                        {typeConfig.label}
                                      </Badge>
                                    )}
                                    <PriorityBadge priority={log.priority} />
                                  </div>
                                  <p className="text-sm text-[#9CA3AF] line-clamp-2">{log.description}</p>
                                </div>
                              </div>
                              
                              {/* Actions */}
                              <div className="flex items-center gap-1">
                                {/* Quick Status Buttons - Always visible for pending/inProgress */}
                                {(log.status === 'pending' || log.status === 'inProgress') && (
                                  <div className="flex items-center gap-1 mr-2 border-r border-[hsl(var(--border))] pr-2">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-8 px-2 text-[#10B981] hover:bg-[#10B981]/20 text-xs font-medium"
                                      onClick={() => handleQuickStatusUpdate(log.id, 'effective')}
                                      title="Mark as Effective"
                                    >
                                      <CheckCircle2 size={14} className="mr-1" />
                                      Effective
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-8 px-2 text-[#E63946] hover:bg-[#E63946]/20 text-xs font-medium"
                                      onClick={() => handleQuickStatusUpdate(log.id, 'notEffective')}
                                      title="Mark as Not Effective"
                                    >
                                      <XCircle size={14} className="mr-1" />
                                      Not Effective
                                    </Button>
                                  </div>
                                )}
                                {/* Other Actions - Show on hover */}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#3B82F6]" onClick={() => openViewModal(log)}>
                                    <Eye size={16} />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#F59E0B]" onClick={() => openEditModal(log)}>
                                    <Edit2 size={16} />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#E63946]" onClick={() => handleDeleteLog(log.id)}>
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Action Taken */}
                            {log.actionTaken && (
                              <div className="bg-[hsl(var(--surface))] rounded-lg p-3 mb-3 border border-[hsl(var(--border))]">
                                <p className="text-[10px] uppercase text-[#6B7280] mb-1">Action Taken</p>
                                <div className="text-sm text-white whitespace-pre-line">{log.actionTaken}</div>
                              </div>
                            )}
                            
                            {/* Metric & Results */}
                            {log.metric && (
                              <div className="bg-gradient-to-r from-[#8B5CF6]/10 to-[#3B82F6]/10 rounded-lg p-3 mb-3 border border-[#8B5CF6]/30">
                                <p className="text-[10px] uppercase text-[#8B5CF6] mb-1">Data Optimization</p>
                                <div className="flex items-center gap-4">
                                  <div>
                                    <span className="text-sm font-medium text-white">{log.metric}</span>
                                    <span className="text-sm text-[#6B7280] mx-2">•</span>
                                    <span className="text-sm text-[#6B7280]">Before: </span>
                                    <span className="text-sm font-mono text-white">{log.currentScore}</span>
                                  </div>
                                  {log.endResult && (
                                    <>
                                      <ArrowRight size={16} className="text-[#6B7280]" />
                                      <div>
                                        <span className="text-sm text-[#6B7280]">After: </span>
                                        <span className="text-sm font-mono font-semibold text-[#10B981]">{log.endResult}</span>
                                      </div>
                                      <ResultIndicator before={log.currentScore} after={log.endResult} />
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Expected Outcome */}
                            {log.expectedOutcome && (
                              <div className="bg-[#10B981]/10 rounded-lg p-3 mb-3 border border-[#10B981]/30">
                                <p className="text-[10px] uppercase text-[#10B981] mb-1">Expected Outcome</p>
                                <p className="text-sm text-white">{log.expectedOutcome}</p>
                              </div>
                            )}
                            
                            {/* Footer */}
                            <div className="flex items-center gap-4 text-xs text-[#6B7280] pt-2 border-t border-[hsl(var(--border))]">
                              <div className="flex items-center gap-1.5">
                                <Calendar size={12} />
                                <span>{new Date(log.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <User size={12} />
                                <span>{log.author}</span>
                              </div>
                              {log.updatedAt && log.updatedAt !== log.date && (
                                <div className="flex items-center gap-1.5">
                                  <RefreshCw size={12} />
                                  <span>Updated: {new Date(log.updatedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                                </div>
                              )}
                              {log.reviewDate && (
                                <div className="flex items-center gap-1.5 text-[#F59E0B]">
                                  <Target size={12} />
                                  <span>Review: {new Date(log.reviewDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>
      
      {/* Add/Edit Log Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
              {editingLog ? <Edit2 size={20} className="text-[#F59E0B]" /> : <Plus size={20} className="text-[#10B981]" />}
              {editingLog ? 'Edit Log Entry' : 'New Log Entry'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Row 1: Date, Author, Priority, Impact */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <Label className="text-[10px] text-[#6B7280]">Date *</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })} 
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]" 
                />
              </div>
              <div>
                <Label className="text-[10px] text-[#6B7280]">Author *</Label>
                <Input 
                  value={formData.author} 
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })} 
                  placeholder="Your name"
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" 
                />
              </div>
              <div>
                <Label className="text-[10px] text-[#6B7280]">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="high" className="text-white">High</SelectItem>
                    <SelectItem value="medium" className="text-white">Medium</SelectItem>
                    <SelectItem value="low" className="text-white">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-[#6B7280]">Impact Level</Label>
                <Select value={formData.impact || 'medium'} onValueChange={(v) => setFormData({ ...formData, impact: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="critical" className="text-white">Critical</SelectItem>
                    <SelectItem value="high" className="text-white">High</SelectItem>
                    <SelectItem value="medium" className="text-white">Medium</SelectItem>
                    <SelectItem value="low" className="text-white">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 2: Issue Type, Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] text-[#6B7280]">Issue Type</Label>
                <Select value={formData.issueType} onValueChange={(v) => setFormData({ ...formData, issueType: v, otherIssueType: '' })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] max-h-[250px]">
                    {issueTypes[activeDepartment]?.map(type => (
                      <SelectItem key={type.id} value={type.id} className="text-white">{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.issueType?.includes('other') && (
                  <Input value={formData.otherIssueType || ''} onChange={e => setFormData({ ...formData, otherIssueType: e.target.value })}
                    placeholder="Specify issue type..." className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1.5 text-xs" />
                )}
              </div>
              <div>
                <Label className="text-[10px] text-[#6B7280]">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    <SelectItem value="pending" className="text-white">Pending</SelectItem>
                    <SelectItem value="inProgress" className="text-white">In Progress</SelectItem>
                    <SelectItem value="effective" className="text-white">Effective</SelectItem>
                    <SelectItem value="notEffective" className="text-white">Not Effective</SelectItem>
                    <SelectItem value="cancelled" className="text-white">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Title */}
            <div>
              <Label className="text-[10px] text-[#6B7280]">Title *</Label>
              <Input 
                value={formData.title} 
                onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
                placeholder="Brief title for the log"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1" 
              />
            </div>
            
            {/* Description */}
            <div>
              <Label className="text-[10px] text-[#6B7280]">Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                placeholder="Describe the issue or optimization"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[80px]" 
              />
            </div>
            
            {/* Action Taken */}
            <div>
              <Label className="text-[10px] text-[#6B7280]">Action Taken</Label>
              <Textarea 
                value={formData.actionTaken} 
                onChange={(e) => setFormData({ ...formData, actionTaken: e.target.value })} 
                placeholder="What actions were taken?"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[80px]" 
              />
            </div>

            {/* Attachments */}
            <div>
              <Label className="text-[10px] text-[#6B7280] flex items-center gap-1"><Camera size={10} /> Screenshots / Evidence</Label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {(formData.attachments || []).map((att, i) => (
                  <div key={i} className="relative group w-20 h-20 rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                    <img src={att} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setFormData({ ...formData, attachments: formData.attachments.filter((_, j) => j !== i) })}
                      className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={8} /></button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/40 flex flex-col items-center justify-center cursor-pointer transition-all">
                  <Camera size={16} className="text-[hsl(var(--text-muted))]" />
                  <span className="text-[8px] text-[hsl(var(--text-muted))] mt-0.5">Upload</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const img = new Image();
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        const maxW = 400;
                        const scale = Math.min(maxW / img.width, 1);
                        canvas.width = img.width * scale;
                        canvas.height = img.height * scale;
                        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
                        const compressed = canvas.toDataURL('image/jpeg', 0.6);
                        setFormData(prev => ({ ...prev, attachments: [...(prev.attachments || []), compressed] }));
                      };
                      img.src = ev.target.result;
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }} />
                </label>
              </div>
            </div>
            
            {/* Data Optimization */}
            <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-lg border border-[hsl(var(--border))]">
              <p className="text-xs font-semibold text-[#8B5CF6] mb-3 flex items-center gap-2">
                <BarChart3 size={14} />Data Optimization (Optional)
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Metric</Label>
                  <Select value={formData.metric} onValueChange={(v) => setFormData({ ...formData, metric: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white mt-1">
                      <SelectValue placeholder="Select metric" />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      {metricOptions[activeDepartment]?.map(m => (
                        <SelectItem key={m} value={m} className="text-white">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Current Score (Before)</Label>
                  <Input 
                    value={formData.currentScore} 
                    onChange={(e) => setFormData({ ...formData, currentScore: e.target.value })} 
                    placeholder="e.g., 12.5 or 15%"
                    className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white mt-1 font-mono" 
                  />
                </div>
              </div>
            </div>
            
            {/* Expected Outcome */}
            <div>
              <Label className="text-[10px] text-[#6B7280]">Expected Outcome</Label>
              <Textarea 
                value={formData.expectedOutcome} 
                onChange={(e) => setFormData({ ...formData, expectedOutcome: e.target.value })} 
                placeholder="What result do you expect from this adjustment?"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px]" 
              />
            </div>
            
            {/* End Results */}
            <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-lg border border-[hsl(var(--border))]">
              <p className="text-xs font-semibold text-[#10B981] mb-3 flex items-center gap-2">
                <Target size={14} />End Results (Fill after review)
              </p>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">End Result/Score</Label>
                  <Input 
                    value={formData.endResult} 
                    onChange={(e) => setFormData({ ...formData, endResult: e.target.value })} 
                    placeholder="Score after adjustment"
                    className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white mt-1 font-mono" 
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Review Date</Label>
                  <Input 
                    type="date" 
                    value={formData.reviewDate} 
                    onChange={(e) => setFormData({ ...formData, reviewDate: e.target.value })} 
                    className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]" 
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-[#6B7280]">Remarks</Label>
                <Textarea 
                  value={formData.remarks} 
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} 
                  placeholder="Additional notes about the results"
                  className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px]" 
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-[hsl(var(--border))] text-white">
              Cancel
            </Button>
            <Button 
              onClick={editingLog ? handleEditLog : handleAddLog} 
              className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white"
            >
              <Check size={16} className="mr-2" />
              {editingLog ? 'Save Changes' : 'Create Log'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Log Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="bg-[#0a0a0c] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b border-[hsl(var(--border))]">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6]/20 to-[#8B5CF6]/20 flex items-center justify-center border border-[#3B82F6]/30">
                <FileText size={24} className="text-[#3B82F6]" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-white flex items-center gap-2">
                  {viewingLog?.title}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={viewingLog?.status} />
                  <PriorityBadge priority={viewingLog?.priority} />
                  {viewingLog?.issueType && (
                    <Badge className="text-[9px] px-1.5 py-0 bg-[#6B7280]/20 text-[#9CA3AF]">
                      {issueTypes[activeDepartment]?.find(t => t.id === viewingLog.issueType)?.label}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </DialogHeader>
          
          {viewingLog && (
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              {/* Meta Info */}
              <div className="flex items-center gap-4 text-sm text-[#6B7280]">
                <div className="flex items-center gap-1.5">
                  <Calendar size={14} />
                  <span>{new Date(viewingLog.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <User size={14} />
                  <span>{viewingLog.author}</span>
                </div>
              </div>
              
              {/* Description */}
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-lg border border-[hsl(var(--border))]">
                <p className="text-[10px] uppercase text-[#6B7280] mb-2">Description</p>
                <p className="text-sm text-white">{viewingLog.description || '-'}</p>
              </div>
              
              {/* Action Taken */}
              <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-lg border border-[hsl(var(--border))]">
                <p className="text-[10px] uppercase text-[#6B7280] mb-2">Action Taken</p>
                <div className="text-sm text-white whitespace-pre-line">{viewingLog.actionTaken || '-'}</div>
              </div>
              
              {/* Data Optimization */}
              {viewingLog.metric && (
                <div className="bg-gradient-to-r from-[#8B5CF6]/10 to-[#3B82F6]/10 p-4 rounded-lg border border-[#8B5CF6]/30">
                  <p className="text-[10px] uppercase text-[#8B5CF6] mb-3">Data Optimization</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-[#6B7280]">Metric</p>
                      <p className="text-sm font-medium text-white">{viewingLog.metric}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">Before</p>
                      <p className="text-lg font-mono font-bold text-white">{viewingLog.currentScore || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#6B7280]">After</p>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-mono font-bold text-[#10B981]">{viewingLog.endResult || '-'}</p>
                        <ResultIndicator before={viewingLog.currentScore} after={viewingLog.endResult} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Expected Outcome */}
              <div className="bg-[#10B981]/10 p-4 rounded-lg border border-[#10B981]/30">
                <p className="text-[10px] uppercase text-[#10B981] mb-2">Expected Outcome</p>
                <p className="text-sm text-white">{viewingLog.expectedOutcome || '-'}</p>
              </div>
              
              {/* Remarks */}
              {viewingLog.remarks && (
                <div className="bg-[hsl(var(--surface-elevated))] p-4 rounded-lg border border-[hsl(var(--border))]">
                  <p className="text-[10px] uppercase text-[#6B7280] mb-2">Remarks</p>
                  <p className="text-sm text-white">{viewingLog.remarks}</p>
                </div>
              )}
              
              {/* Review Date */}
              {viewingLog.reviewDate && (
                <div className="flex items-center gap-2 text-sm text-[#F59E0B]">
                  <Target size={16} />
                  <span>Review Date: {new Date(viewingLog.reviewDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="pt-4 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setShowViewModal(false)} className="border-[hsl(var(--border))] text-white">
              Close
            </Button>
            <Button 
              onClick={() => { setShowViewModal(false); openEditModal(viewingLog); }} 
              className="bg-gradient-to-r from-[#F59E0B] to-[#E63946] text-white"
            >
              <Edit2 size={16} className="mr-2" />Edit Log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
